/**
 * AssetPublicView.jsx — QR code landing page
 * Route: /a/:assetCode  (no auth required)
 *
 * ▸ Internal employee  (iassets_token in localStorage) → full card
 * ▸ External / unknown (no token)                      → limited card
 *
 * Fully responsive: optimised for mobile browsers opened from QR scan,
 * also works correctly in desktop WebView and wide browsers.
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

/* ── API URL (works on every environment) ───────────────── */
function resolveApiBase() {
  if (process.env.REACT_APP_API_URL)
    return process.env.REACT_APP_API_URL.replace(/\/$/, '');
  const { protocol, hostname } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1')
    return 'http://localhost:8000/api';
  return `${protocol}//${hostname}/api`;   // same-domain reverse-proxy
}
const API      = resolveApiBase();
const IMG_BASE = API.replace('/api', '');

const isInternal = () => Boolean(localStorage.getItem('iassets_token'));

const getAuthHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem('iassets_token')}`,
});

const getCurrentUser = () => {
  try { return JSON.parse(localStorage.getItem('iassets_user') || '{}'); }
  catch { return {}; }
};

const ST = {
  operational: { bg:'#dcfce7', c:'#16a34a', dot:'#16a34a', label:'Operational'       },
  maintenance:  { bg:'#fef3c7', c:'#d97706', dot:'#d97706', label:'Under Maintenance' },
  breakdown:    { bg:'#fee2e2', c:'#dc2626', dot:'#dc2626', label:'Breakdown'         },
  retired:      { bg:'#f1f5f9', c:'#64748b', dot:'#94a3b8', label:'Retired'           },
};

const fdate = d => d
  ? new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' })
  : '—';

/* Global styles injected once into <head> */
const GLOBAL_CSS = `
  *, *::before, *::after { box-sizing: border-box; }

  /* Prevent iOS from auto-zooming inputs / text */
  html {
    font-size: 16px;
    -webkit-text-size-adjust: 100%;
    text-size-adjust: 100%;
  }
  body { margin: 0; padding: 0; }

  /* Page wrapper — full viewport, safe-area aware */
  .pv-page {
    min-height: 100vh;
    min-height: 100dvh;           /* dynamic viewport on iOS Safari */
    background: linear-gradient(160deg, #1a56db 0%, #1e3a8a 100%);
    display: flex;
    align-items: flex-start;
    justify-content: center;

    /* Honour notch / home-bar on modern phones */
    padding-top:    max(env(safe-area-inset-top,    0px), 20px);
    padding-right:  max(env(safe-area-inset-right,  0px), 12px);
    padding-bottom: max(env(safe-area-inset-bottom, 0px), 32px);
    padding-left:   max(env(safe-area-inset-left,   0px), 12px);
  }

  /* Card */
  .pv-card {
    background: #fff;
    border-radius: 20px;
    width: 100%;
    max-width: 460px;
    box-shadow: 0 32px 80px rgba(0,0,0,.28);
    overflow: visible;          /* was hidden — was clipping buttons */
    /* Slide-up entrance */
    animation: pv-up .35s cubic-bezier(.22,1,.36,1) both;
  }

  /* Sticky back bar on mobile */
  .pv-back-bar {
    display: none;
  }
  @media (max-width: 768px) {
    .pv-back-bar {
      display: flex;
      gap: 8px;
      padding: 10px 14px;
      background: #fff;
      border-bottom: 1px solid #f1f5f9;
      position: sticky;
      top: 0;
      z-index: 10;
    }
    .pv-cta-section {
      padding-bottom: max(env(safe-area-inset-bottom, 0px), 20px);
    }
  }

  @keyframes pv-up {
    from { opacity: 0; transform: translateY(18px); }
    to   { opacity: 1; transform: translateY(0);    }
  }
  @keyframes pv-spin { to { transform: rotate(360deg); } }

  /* Remove blue tap flash on iOS */
  .pv-tap { -webkit-tap-highlight-color: transparent; }

  /* Info tile grid — 2 cols on any phone ≥300px */
  .pv-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
    margin-bottom: 16px;
  }
  @media (max-width: 299px) {
    .pv-grid { grid-template-columns: 1fr; }
  }

  /* Touch-friendly buttons */
  .pv-btn-primary {
    width: 100%;
    padding: 15px;
    background: #1a56db;
    color: #fff;
    border: none;
    border-radius: 14px;
    font-size: 15px;
    font-weight: 700;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    letter-spacing: .01em;
    -webkit-tap-highlight-color: transparent;
    transition: opacity .15s;
  }
  .pv-btn-primary:active { opacity: .85; }

  .pv-btn-danger {
    width: 100%;
    padding: 14px;
    background: #fee2e2;
    color: #dc2626;
    border: 1px solid #fca5a5;
    border-radius: 14px;
    font-size: 15px;
    font-weight: 700;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    text-decoration: none;
    -webkit-tap-highlight-color: transparent;
    transition: opacity .15s;
  }
  .pv-btn-danger:active { opacity: .8; }
`;

export default function AssetPublicView() {
  const { assetCode } = useParams();
  const navigate      = useNavigate();
  const [asset,   setAsset]      = useState(null);
  const [loading, setLoading]    = useState(true);
  const [error,   setError]      = useState(null);
  const [showTicket, setShowTicket] = useState(false);
  const internal = isInternal();

  useEffect(() => {
    /* Inject CSS once */
    if (!document.getElementById('pv-global')) {
      const s = Object.assign(document.createElement('style'), {
        id: 'pv-global', textContent: GLOBAL_CSS,
      });
      document.head.appendChild(s);
    }
    /* Fetch asset */
    axios.get(`${API}/assets/public/${assetCode}`)
      .then(r => setAsset(r.data))
      .catch(e => setError(
        e.code === 'ERR_NETWORK' || e.message?.includes('Network Error')
          ? 'Cannot reach the server. Try again.'
          : e.response?.status === 404 ? 'Asset not found' : 'Failed to load asset'
      ))
      .finally(() => setLoading(false));
  }, [assetCode]);

  /* ── Loading ─────────────────────────────────────────────── */
  if (loading) return (
    <div className="pv-page">
      <div className="pv-card" style={{ padding:'56px 20px', textAlign:'center' }}>
        <div style={{ width:40, height:40, border:'3.5px solid #e2e8f0',
          borderTopColor:'#1a56db', borderRadius:'50%',
          animation:'pv-spin .8s linear infinite', margin:'0 auto 18px' }}/>
        <div style={{ fontSize:14, color:'#94a3b8', fontWeight:500 }}>Loading asset…</div>
      </div>
    </div>
  );

  /* ── Error ───────────────────────────────────────────────── */
  if (error) return (
    <div className="pv-page">
      <div className="pv-card" style={{ padding:'52px 20px', textAlign:'center' }}>
        <div style={{ fontSize:52, marginBottom:14 }}>🔍</div>
        <div style={{ fontSize:18, fontWeight:800, color:'#1e2a3b', marginBottom:8 }}>{error}</div>
        <div style={{ fontSize:13, color:'#94a3b8' }}>
          Code: <code style={{ color:'#1a56db', fontWeight:700 }}>{assetCode}</code>
        </div>
      </div>
    </div>
  );

  const st = ST[asset?.status] || ST.retired;

  /* ── Shared sub-components ───────────────────────────────── */
  const Header = () => (
    <div style={{ background:'#1a56db', padding:'14px 18px',
      display:'flex', alignItems:'center', gap:10 }}>
      {isMobileView && (
        <button onClick={() => navigate(-1)}
          style={{ background:'none', border:'none', cursor:'pointer',
            color:'#fff', fontSize:20, padding:0, lineHeight:1,
            display:'flex', alignItems:'center' }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
            stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 5l-7 7 7 7"/>
          </svg>
        </button>
      )}
      <span style={{ color:'#fff', fontSize:15, fontWeight:900,
        letterSpacing:'.1em', flex:1 }}>
        iAssets
      </span>
      <span style={{ color:'rgba(255,255,255,.6)', fontSize:11, fontWeight:500 }}>
        Asset Management
      </span>
    </div>
  );

  const isMobileView = window.innerWidth <= 768;

  const Footer = () => (
    <div style={{ padding:'12px 18px', borderTop:'1px solid #f1f5f9',
      textAlign:'center', fontSize:11, color:'#94a3b8' }}>
      Powered by <strong style={{ color:'#1a56db' }}>iAssets</strong>
    </div>
  );

  /* ════════════════════════════════════════════════════════════
     EXTERNAL VIEW
  ════════════════════════════════════════════════════════════ */
  if (!internal) return (
    <div className="pv-page">
      <div className="pv-card">
        <Header />

        {/* Icon strip */}
        <div style={{ height:100, background:'linear-gradient(135deg,#f0f4ff,#e8ecf8)',
          display:'flex', alignItems:'center', justifyContent:'center' }}>
          <span style={{ fontSize:54, opacity:.18 }}>⚙</span>
        </div>

        <div style={{ padding:'22px 18px' }}>
          {/* Asset name */}
          <h1 style={{ margin:'0 0 6px', fontSize:clamp(20,22), fontWeight:800,
            color:'#1e2a3b', lineHeight:1.25 }}>
            {asset.name}
          </h1>
          <div style={{ fontSize:14, color:'#1a56db', fontWeight:700,
            fontFamily:'monospace', marginBottom:4 }}>
            {asset.asset_code}
          </div>
          {asset.make && (
            <div style={{ fontSize:13, color:'#94a3b8', marginBottom:18 }}>
              {asset.make}{asset.model ? ` · ${asset.model}` : ''}
            </div>
          )}

          {/* Serial number */}
          {asset.serial_number && (
            <div style={{ background:'#f0f6ff', borderRadius:12, padding:'12px 14px',
              display:'flex', justifyContent:'space-between', alignItems:'center',
              marginBottom:18, border:'1px solid #dbeafe', gap:8, flexWrap:'wrap' }}>
              <span style={{ fontSize:12, color:'#64748b', fontWeight:500 }}>Serial Number</span>
              <span style={{ fontFamily:'monospace', fontSize:14, fontWeight:700,
                color:'#1a56db', wordBreak:'break-all' }}>
                {asset.serial_number}
              </span>
            </div>
          )}

          {/* Restriction notice */}
          <div style={{ background:'#fffbeb', border:'1px solid #fde68a', borderRadius:14,
            padding:'14px', marginBottom:22, display:'flex', gap:12, alignItems:'flex-start' }}>
            <span style={{ fontSize:22, lineHeight:1, flexShrink:0 }}>🔒</span>
            <div>
              <div style={{ fontSize:14, fontWeight:700, color:'#92400e', marginBottom:4 }}>
                Restricted Access
              </div>
              <div style={{ fontSize:12, color:'#b45309', lineHeight:1.6 }}>
                Full asset details are visible to authorised employees only.
                Log in to see complete information.
              </div>
            </div>
          </div>

          <button className="pv-btn-primary pv-tap"
            onClick={() => navigate(`/login?redirect=/a/${assetCode}`)}>
            🔐 Employee Login
          </button>
        </div>

        <Footer />
      </div>
    </div>
  );

  /* ════════════════════════════════════════════════════════════
     INTERNAL VIEW — full card
  ════════════════════════════════════════════════════════════ */
  const infoTiles = [
    ['📍', 'Location',   asset.location_name],
    ['🏷',  'Category',  asset.category_name],
    ['🏢', 'Department', asset.department],
    ['👤', 'Assigned',   asset.assigned_user_name],
    ['📅', 'Registered', fdate(asset.purchase_date)],
    ['🛡',  'Warranty',  fdate(asset.warranty_expiry)],
  ].filter(([,,v]) => v);

  return (
    <>
    <div className="pv-page">
      <div className="pv-card">
        <Header />

        {/* Hero image */}
        <div style={{ position:'relative', overflow:'hidden',
          height:clamp(140,180), background:'linear-gradient(135deg,#f0f4ff,#e8ecf8)' }}>
          {asset.image_url
            ? <img src={`${IMG_BASE}${asset.image_url}`} alt={asset.name}
                style={{ width:'100%', height:'100%',maxWidth:'100%',padding:'5px', objectFit:'contain', display:'block' }}/>
            : <div style={{ height:'100%', display:'flex', alignItems:'center',
                justifyContent:'center', fontSize:72, opacity:.16 }}>⚙</div>
          }
          {/* Status pill */}
          <div style={{ position:'absolute', top:12, right:12,
            background:st.bg, color:st.c, fontSize:11, fontWeight:700,
            padding:'5px 12px', borderRadius:20,
            display:'flex', alignItems:'center', gap:5,
            boxShadow:'0 2px 10px rgba(0,0,0,.12)' }}>
            <span style={{ width:7, height:7, borderRadius:'50%',
              background:st.dot, flexShrink:0 }}/>
            {st.label}
          </div>
        </div>

        <div style={{ padding:'20px 18px' }}>
          {/* Name / code */}
          <div style={{ marginBottom:16 }}>
            <h1 style={{ margin:0, fontSize:clamp(20,22), fontWeight:800,
              color:'#1e2a3b', lineHeight:1.25 }}>
              {asset.name}
            </h1>
            <div style={{ fontSize:14, color:'#1a56db', fontWeight:700,
              fontFamily:'monospace', marginTop:5 }}>
              {asset.asset_code}
            </div>
            {asset.make && (
              <div style={{ fontSize:12, color:'#94a3b8', marginTop:4 }}>
                {asset.make}{asset.model ? ` · ${asset.model}` : ''}
              </div>
            )}
          </div>

          {/* Info tiles */}
          {infoTiles.length > 0 && (
            <div className="pv-grid">
              {infoTiles.map(([icon, label, value]) => (
                <div key={label} style={{ background:'#f8fafc', borderRadius:12,
                  padding:'11px 12px', border:'1px solid #f1f5f9' }}>
                  <div style={{ fontSize:10, color:'#94a3b8', fontWeight:600,
                    marginBottom:4, display:'flex', alignItems:'center', gap:4 }}>
                    <span>{icon}</span>{label}
                  </div>
                  <div style={{ fontSize:13, fontWeight:600, color:'#1e2a3b',
                    wordBreak:'break-word', lineHeight:1.35 }}>
                    {value}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Serial number */}
          {asset.serial_number && (
            <div style={{ background:'#f0f6ff', borderRadius:12, padding:'12px 14px',
              display:'flex', justifyContent:'space-between', alignItems:'center',
              marginBottom:16, border:'1px solid #dbeafe', gap:8, flexWrap:'wrap' }}>
              <span style={{ fontSize:12, color:'#64748b', fontWeight:500 }}>Serial Number</span>
              <span style={{ fontFamily:'monospace', fontSize:14, fontWeight:700,
                color:'#1a56db', wordBreak:'break-all' }}>
                {asset.serial_number}
              </span>
            </div>
          )}

          {/* Critical badge */}
          {asset.is_critical && (
            <div style={{ background:'#fee2e2', border:'1px solid #fca5a5', borderRadius:12,
              padding:'12px 14px', marginBottom:16,
              display:'flex', alignItems:'center', gap:8,
              fontSize:13, color:'#dc2626', fontWeight:600 }}>
              ⚡ Critical Asset — Priority maintenance
            </div>
          )}

          {/* Description */}
          {asset.description && (
            <div style={{ background:'#f8fafc', borderRadius:12, padding:'12px 14px',
              fontSize:13, color:'#64748b', lineHeight:1.65, marginBottom:16,
              border:'1px solid #f1f5f9' }}>
              {asset.description}
            </div>
          )}

          <div style={{ height:1, background:'#f1f5f9', margin:'4px 0 16px' }}/>

          {/* Sticky back bar — mobile only */}
          <div className="pv-back-bar">
            <button onClick={() => navigate(-1)}
              style={{ flex:1, padding:'9px 0', border:'1px solid #e2e8f0',
                borderRadius:10, background:'#f8fafc', fontSize:12,
                fontWeight:600, color:'#475569', cursor:'pointer',
                display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
              ← Back
            </button>
            <button onClick={() => navigate('/mobile')}
              style={{ flex:1, padding:'9px 0', border:'1px solid #e2e8f0',
                borderRadius:10, background:'#f8fafc', fontSize:12,
                fontWeight:600, color:'#475569', cursor:'pointer',
                display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
              🏠 Home
            </button>
          </div>

          {/* CTAs */}
          <div className="pv-cta-section" style={{ padding:'0 18px 20px' }}>
            <button className="pv-btn-primary pv-tap"
              onClick={() => navigate(`/a/${assetCode}/profile`)}
              style={{ marginBottom:10 }}>
              📋 View Full Profile
            </button>

            <button className="pv-btn-danger pv-tap"
              onClick={() => setShowTicket(true)}>
              🎫 Raise Ticket
            </button>
          </div>
        </div>

        <Footer />
      </div>
    </div>

    {/* Raise Ticket Modal */}
    {showTicket && (
      <RaiseTicketModal
        asset={asset}
        onClose={() => setShowTicket(false)}
      />
    )}
    </>
  );
}

/* ════════════════════════════════════════════════════════════
   RAISE TICKET MODAL
   Full-screen modal on mobile, centred card on desktop.
   Fields: Ticket Type, Failure Type, Priority, Description,
           Reported By, Contact Number, Due Date, Duration.
════════════════════════════════════════════════════════════ */
function RaiseTicketModal({ asset, onClose }) {
  const currentUser = getCurrentUser();

  /* masters */
  const [ticketTypes,  setTicketTypes]  = useState([]);
  const [failureTypes, setFailureTypes] = useState([]);
  const [users,        setUsers]        = useState([]);

  /* form */
  const today = new Date().toISOString().slice(0, 10);

  const [ticketType,        setTicketType]       = useState('breakdown');
  const [maintenanceTypeId, setMaintenanceTypeId] = useState('');
  const [failureTypeId,     setFailureTypeId]     = useState('');
  const [priority,          setPriority]          = useState('high');
  const [description,       setDescription]       = useState('');
  const [reportedBy,        setReportedBy]        = useState(currentUser?.name || '');
  const [contactNo,         setContactNo]         = useState(currentUser?.phone || '');
  const [dueDate,           setDueDate]           = useState(today);
  const [dueTime,           setDueTime]           = useState('');
  const [duration,          setDuration]          = useState('');
  /* Auto-assign from asset master */
  const [assignTo,          setAssignTo]          = useState(
    String(asset?.assigned_to || asset?.responsible_person || asset?.employee_id || '')
  );
  /* Breakdown ID — from WO Numbering Series */
  const [breakdownId,       setBreakdownId]       = useState('Loading…');
  const [saving,            setSaving]            = useState(false);
  const [done,              setDone]              = useState(null);

  const isBreakdown = ticketType === 'breakdown';

  /* Fetch next WO number from Numbering Series on mount */
  useEffect(() => {
    const token = localStorage.getItem('iassets_token');
    fetch(`${API}/masters/wo-numbering-series/next-number`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => setBreakdownId(d?.next_number || '— Not configured —'))
      .catch(() => setBreakdownId('— Not configured —'));
  }, []);

  /* load masters */
  useEffect(() => {
    const hdrs = { headers: getAuthHeader() };
    axios.get(`${API}/masters/maintenance-types`, hdrs)
      .then(r => setTicketTypes(r.data || [])).catch(() => {});
    axios.get(`${API}/masters/failure-types`, hdrs)
      .then(r => setFailureTypes(r.data || [])).catch(() => {});
    axios.get(`${API}/users/`, hdrs)
      .then(r => setUsers(r.data?.data || r.data || [])).catch(() => {});
  }, []);

  /* Auto-select first matching maintenance type when ticketType changes */
  useEffect(() => {
    if (!ticketTypes.length) return;
    const field = WO_TYPE_FIELD[ticketType];
    const match = ticketTypes.find(t => field && (t[field] === true || t[field] === 1));
    setMaintenanceTypeId(match ? String(match.id) : '');
  }, [ticketType, ticketTypes]);

  /* submit */
  const submit = async () => {
    if (!description.trim()) { alert('Please describe the issue'); return; }
    setSaving(true);
    try {
      const payload = {
        title:               `${ticketType.toUpperCase()} — ${asset.asset_code}`,
        asset_id:            asset.id,
        type:                ticketType,
        maintenance_type_id: maintenanceTypeId ? parseInt(maintenanceTypeId) : null,
        priority,
        due_date:           dueDate ? dueDate.slice(0,10) : null,
        assigned_to:        assignTo ? parseInt(assignTo) : null,
        description,
        estimated_duration: duration || null,
        // breakdown fields
        ...(isBreakdown && {
          breakdown_id:    breakdownId,
          failure_type_id: failureTypeId ? parseInt(failureTypeId) : null,
          reported_by:     reportedBy || null,
          contact_number:  contactNo  || null,
          due_time:        dueTime    || null,
        }),
      };

      const r = await axios.post(`${API}/work-orders/`, payload,
        { headers: getAuthHeader() });
      setDone({ wo_id: r.data?.id, title: payload.title });
    } catch(e) {
      alert(e?.response?.data?.detail || 'Failed to raise ticket. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  /* ── styles ── */
  const ii = {
    width: '100%', border: '1.5px solid #e2e8f0', borderRadius: 10,
    padding: '10px 13px', fontSize: 15, outline: 'none',
    boxSizing: 'border-box', background: '#fff', color: '#1a1f2e',
    WebkitAppearance: 'none', appearance: 'none',
  };
  const lbl = {
    display: 'block', fontSize: 12, fontWeight: 700,
    color: '#475569', marginBottom: 5, textTransform: 'uppercase',
    letterSpacing: '.05em',
  };
  const row2 = {
    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12,
  };

  /* Map ticket type key → DB column that filters maintenance types */
  const WO_TYPE_FIELD = {
    breakdown:  'is_breakdown',
    pm:         'is_schedule_pm',
    inspection: 'is_inspection',
    repair:     'is_repair',
  };

  /* Filtered list — only types applicable to the selected ticket type */
  const filteredMaintenanceTypes = ticketTypes.filter(t => {
    const field = WO_TYPE_FIELD[ticketType];
    if (!field) return true;                    // unknown type → show all
    return t[field] === true || t[field] === 1; // only matching types
  });

  /* Ticket type icons */
  const TYPE_CFG = {
    breakdown:  { icon: '⚡', label: 'Breakdown',          color: '#dc2626', bg: '#fff5f5' },
    pm:         { icon: '🔧', label: 'Preventive (PM)',    color: '#1a56db', bg: '#f0f6ff' },
    inspection: { icon: '🔍', label: 'Inspection',         color: '#15803d', bg: '#f0fdf4' },
    repair:     { icon: '✱',  label: 'Repair',             color: '#b45309', bg: '#fefce8' },
  };
  const cfg = TYPE_CFG[ticketType] || TYPE_CFG.breakdown;

  /* ── Success screen ── */
  if (done) return (
    <div style={OVERLAY} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ ...CARD, maxHeight: '60vh', justifyContent: 'center', alignItems: 'center' }}>
        <div style={{ textAlign: 'center', padding: '32px 24px' }}>
          <div style={{ fontSize: 64, marginBottom: 16 }}>✅</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: '#1a1f2e', marginBottom: 8 }}>
            Ticket Raised!
          </div>
          <div style={{ fontSize: 13, color: '#64748b', marginBottom: 6 }}>
            {done.title}
          </div>
          {done.wo_id && (
            <div style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 700,
              color: '#1a56db', marginBottom: 24 }}>
              WO-{String(done.wo_id).padStart(6, '0')}
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <button onClick={onClose}
              style={{ padding: '12px', background: '#1a56db', color: '#fff',
                border: 'none', borderRadius: 12, fontSize: 14, fontWeight: 700,
                cursor: 'pointer' }}>
              Done
            </button>
            <button onClick={() => {
              setDone(null);
              setDescription('');
              /* Re-fetch next number for the new ticket */
              const token = localStorage.getItem('iassets_token');
              fetch(`${API}/masters/wo-numbering-series/next-number`, {
                headers: { Authorization: `Bearer ${token}` }
              })
                .then(r => r.ok ? r.json() : null)
                .then(d => setBreakdownId(d?.next_number || '— Not configured —'))
                .catch(() => setBreakdownId('— Not configured —'));
            }}
              style={{ padding: '12px', background: '#f1f5f9', color: '#374151',
                border: 'none', borderRadius: 12, fontSize: 14, fontWeight: 600,
                cursor: 'pointer' }}>
              Raise Another Ticket
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  /* ── Main form ── */
  return (
    <div style={OVERLAY} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={CARD}>

        {/* Header */}
        <div style={{ background: cfg.bg, padding: '16px 18px 14px',
          borderBottom: '1px solid #e2e8f0', flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: 17, fontWeight: 800, color: cfg.color, display: 'flex', gap: 8 }}>
                {cfg.icon} Raise Ticket
              </div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 3 }}>
                {asset.name} · <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{asset.asset_code}</span>
              </div>
            </div>
            <button onClick={onClose}
              style={{ background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 22, color: '#94a3b8', lineHeight: 1, padding: 4 }}>✕</button>
          </div>

          {/* Ticket type selector */}
          <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
            {Object.entries(TYPE_CFG).map(([key, t]) => (
              <button key={key} onClick={() => setTicketType(key)}
                style={{ padding: '7px 14px', borderRadius: 20, fontSize: 12, fontWeight: 700,
                  cursor: 'pointer', border: 'none', transition: 'all .15s',
                  background: ticketType === key ? t.color : '#fff',
                  color:      ticketType === key ? '#fff'  : '#64748b',
                  boxShadow:  ticketType === key ? `0 2px 8px ${t.color}55` : 'none',
                  border:     ticketType === key ? 'none' : '1px solid #e2e8f0',
                }}>
                {t.icon} {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Scrollable body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 18px',
          display: 'flex', flexDirection: 'column', gap: 14,
          WebkitOverflowScrolling: 'touch' }}>

          {/* Breakdown-specific fields */}
          {isBreakdown && (
            <div style={{ background: '#fff5f5', border: '1px solid #fecaca',
              borderRadius: 12, padding: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#dc2626',
                textTransform: 'uppercase', letterSpacing: '.06em' }}>
                ⚡ Breakdown Details
              </div>
              <div>
                <label style={lbl}>Breakdown ID</label>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                  background:'#eff6ff', border:'1px solid #bfdbfe',
                  borderRadius:8, padding:'10px 14px' }}>
                  <div style={{ fontSize:11, color:'#1a56db' }}>
                    From WO Numbering Series
                  </div>
                  <div style={{ fontSize:16, fontWeight:800, color:'#1a56db',
                    fontFamily:'monospace', letterSpacing:'.04em' }}>
                    {breakdownId === 'Loading…'
                      ? <span style={{ fontSize:12, color:'#94a3b8' }}>Loading…</span>
                      : breakdownId === '— Not configured —'
                        ? <span style={{ fontSize:11, color:'#f59e0b' }}>⚠ Not configured</span>
                        : breakdownId}
                  </div>
                </div>
              </div>
              <div>
                <label style={lbl}>Failure Type</label>
                <select value={failureTypeId} onChange={e => setFailureTypeId(e.target.value)} style={ii}>
                  <option value="">— Select Failure Type —</option>
                  {failureTypes.filter(f => f.is_active).map(f => (
                    <option key={f.id} value={f.id}>{f.name}</option>
                  ))}
                </select>
              </div>
              <div style={row2}>
                <div>
                  <label style={lbl}>Reported By</label>
                  <input value={reportedBy} onChange={e => setReportedBy(e.target.value)}
                    placeholder="Your name" style={ii}/>
                </div>
                <div>
                  <label style={lbl}>Contact No.</label>
                  <input type="tel" value={contactNo} onChange={e => setContactNo(e.target.value)}
                    placeholder="+91 98765…" style={ii}/>
                </div>
              </div>
            </div>
          )}

          {/* Priority */}
          <div>
            <label style={lbl}>Priority</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {[
                { v:'low',      label:'Low',      c:'#64748b', bg:'#f1f5f9' },
                { v:'medium',   label:'Medium',   c:'#d97706', bg:'#fef3c7' },
                { v:'high',     label:'High',     c:'#dc2626', bg:'#fee2e2' },
                { v:'critical', label:'Critical', c:'#7c3aed', bg:'#ede9fe' },
              ].map(p => (
                <button key={p.v} onClick={() => setPriority(p.v)}
                  style={{ flex: 1, padding: '8px 4px', borderRadius: 8, fontSize: 12,
                    fontWeight: 700, cursor: 'pointer', border: 'none', transition: 'all .15s',
                    background: priority === p.v ? p.c : '#fff',
                    color:      priority === p.v ? '#fff' : p.c,
                    border:     priority === p.v ? 'none' : `1px solid ${p.c}44`,
                  }}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Description */}
          <div>
            <label style={lbl}>Description / Problem Statement *</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)}
              rows={4} placeholder={isBreakdown
                ? 'Describe the breakdown — what happened, when, any unusual sounds/smells…'
                : 'Describe the work needed or issue observed…'}
              style={{ ...ii, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }}/>
          </div>

          {/* Assign To */}
          <div>
            <label style={{ ...lbl, display:'flex', alignItems:'center', gap:6 }}>
              Assign To
              {assignTo && (
                <span style={{ fontSize:10, color:'#16a34a', background:'#f0fdf4',
                  padding:'1px 7px', borderRadius:5, fontWeight:600 }}>
                  ✓ From Asset
                </span>
              )}
            </label>
            <select value={assignTo} onChange={e => setAssignTo(e.target.value)} style={ii}>
              <option value="">— Auto-assign —</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>

          {/* Due Date + Time */}
          <div style={row2}>
            <div>
              <label style={lbl}>Due Date</label>
              <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} style={ii}/>
            </div>
            <div>
              <label style={lbl}>{isBreakdown ? 'Time' : 'Estimated Duration'}</label>
              {isBreakdown ? (
                <input type="time" value={dueTime} onChange={e => setDueTime(e.target.value)} style={ii}/>
              ) : (
                <select value={duration} onChange={e => setDuration(e.target.value)} style={ii}>
                  <option value="">— Select —</option>
                  <option value="< 2 Hours">&lt; 2 Hours</option>
                  <option value="2–4 Hours">2–4 Hours</option>
                  <option value="4–8 Hours">4–8 Hours</option>
                  <option value="1–3 Days">1–3 Days</option>
                  <option value="> 3 Days">&gt; 3 Days</option>
                </select>
              )}
            </div>
          </div>

          {/* Maintenance type — filtered by selected ticket type */}
          {ticketTypes.length > 0 && (
            <div>
              <label style={lbl}>Ticket Type / Maintenance Type</label>
              <select style={ii}
                value={maintenanceTypeId}
                onChange={e => {
                  setMaintenanceTypeId(e.target.value);
                  const selected = ticketTypes.find(t => String(t.id) === e.target.value);
                  if (selected) {
                    const field = WO_TYPE_FIELD[ticketType];
                    if (field) setTicketType(
                      Object.entries(WO_TYPE_FIELD).find(([,f]) => f === field)?.[0] || ticketType
                    );
                  }
                }}>
                <option value="">
                  {filteredMaintenanceTypes.length === 0
                    ? '— No types configured for this category —'
                    : '— Select Maintenance Type —'}
                </option>
                {filteredMaintenanceTypes
                  .filter(t => t.is_active !== false && t.is_active !== 0)
                  .map(t => (
                    <option key={t.id} value={String(t.id)}>{t.name}</option>
                  ))}
              </select>
              {filteredMaintenanceTypes.filter(t => t.is_active !== false && t.is_active !== 0).length === 0 && (
                <div style={{ fontSize:11, color:'#f59e0b', marginTop:4 }}>
                  ⚠ No maintenance types are configured for <strong>{TYPE_CFG[ticketType]?.label}</strong>.
                  Please update the Maintenance Type Master in Setup.
                </div>
              )}
            </div>
          )}

        </div>

        {/* Footer */}
        <div style={{ padding: '14px 18px',
          paddingBottom: `max(env(safe-area-inset-bottom, 0px), 14px)`,
          borderTop: '1px solid #e2e8f0', flexShrink: 0 }}>
          <button onClick={submit} disabled={saving}
            style={{ width: '100%', padding: '14px',
              background: saving ? '#93c5fd' : cfg.color, color: '#fff',
              border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 700,
              cursor: saving ? 'not-allowed' : 'pointer', transition: 'all .15s',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            {saving ? (
              <>
                <span style={{ width: 18, height: 18, border: '2px solid rgba(255,255,255,.4)',
                  borderTopColor: '#fff', borderRadius: '50%',
                  animation: 'pv-spin .8s linear infinite', display: 'inline-block' }}/>
                Submitting…
              </>
            ) : (
              `${cfg.icon} Submit ${cfg.label} Ticket`
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

/* Modal overlay + card styles */
const OVERLAY = {
  position: 'fixed', inset: 0,
  background: 'rgba(10,14,26,.6)',
  backdropFilter: 'blur(4px)',
  WebkitBackdropFilter: 'blur(4px)',
  zIndex: 1400,
  display: 'flex',
  alignItems: 'flex-end',      /* bottom-sheet on mobile */
  justifyContent: 'center',
  padding: 0,
};
const CARD = {
  background: '#fff',
  borderRadius: '20px 20px 0 0',
  width: '100%',
  maxWidth: 540,
  maxHeight: '94dvh',
  maxHeight: '94vh',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '0 -8px 40px rgba(0,0,0,.2)',
  overflow: 'hidden',
};

/* clamp(min, max) — returns max on desktop, scales on small screens */
function clamp(min, max) {
  return `clamp(${min}px, ${min + (max - min) * 0.5}vw + ${min * 0.5}px, ${max}px)`;
}
